import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../components/AuthContext';
import TileSelectionModal from '../../components/TileSelectionModal';
import { 
  FaPlus, 
  FaHashtag, 
  FaTimes, 
  FaEye, 
  FaFilter, 
  FaDownload,
  FaBox,
  FaPuzzlePiece,
  FaWarehouse,
  FaTruck,
  FaCalendarAlt,
  FaFileAlt,
  FaSave,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaStickyNote,
  FaTrash,
  FaFileExport,
  FaUndo,
  FaBoxes,
  FaLayerGroup,
  FaUser,
  FaCog,
  FaTh,
  FaList,
  FaArrowUp,
  FaChevronUp,
  FaChevronDown,
  FaSync
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Select from 'react-select';

const TilesOutward = () => {
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{
    tileId: null,
    tileName: '',
    tileDetails: null,
    freshStockBox: 0,
    freshStockPieces: 0,
    cornerDamagedBox: 0,
    cornerDamagedPieces: 0,
    availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 }
  }]);

  const [showTilesModal, setShowTilesModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  
  // Outward history state
  const [outwardHistory, setOutwardHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const totalHistoryPages = Math.ceil(totalHistory / historyLimit);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOutward, setSelectedOutward] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    location: null,
    startDate: null,
    endDate: null
  });

  // Helper function to format date as DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [viewMode, setViewMode] = useState('list');

  // Refs for focus management
  const freshStockBoxRefs = useRef([]);
  const freshStockPiecesRefs = useRef([]);
  const cornerDamagedBoxRefs = useRef([]);
  const cornerDamagedPiecesRefs = useRef([]);

  // Initial data loading
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load outward history when filters or pagination changes
  useEffect(() => {
    fetchOutwardHistory();
  }, [historyPage, historyLimit, historyFilters]);

  // Update available stock when location changes
  useEffect(() => {
    if (selectedLocation) {
      const updateAvailableStock = async () => {
        const updatedItems = await Promise.all(
          items.map(async (item) => {
            if (item.tileId) {
              const stock = await fetchTileStock(item.tileId, selectedLocation.value);
              return { ...item, availableStock: stock };
            }
            return item;
          })
        );
        setItems(updatedItems);
      };
      updateAvailableStock();
    }
  }, [selectedLocation]);

  // Update refs when items change
  useEffect(() => {
    freshStockBoxRefs.current = freshStockBoxRefs.current.slice(0, items.length);
    freshStockPiecesRefs.current = freshStockPiecesRefs.current.slice(0, items.length);
    cornerDamagedBoxRefs.current = cornerDamagedBoxRefs.current.slice(0, items.length);
    cornerDamagedPiecesRefs.current = cornerDamagedPiecesRefs.current.slice(0, items.length);
  }, [items.length]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [tilesRes, locationsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles?userId=${user.user._id}&includeNoStock=true`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${user.user.company._id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (tilesRes.ok) {
        const tilesData = await tilesRes.json();
        const rawTiles = Array.isArray(tilesData) ? tilesData : tilesData.data || [];
        
        // Remove brand images to optimize payload (same as TilesDeliveryOrderNew)
        const processedTiles = rawTiles.map(tile => {
          const cleanTile = { ...tile };
          
          // Remove brand image
          if (cleanTile.brand?.image) {
            delete cleanTile.brand.image;
          }
          
          // Remove any other heavy image properties
          if (cleanTile.tilesImage) {
            cleanTile.tilesImage = Boolean(cleanTile.tilesImage);
          }
          
          if (cleanTile.imageData) {
            delete cleanTile.imageData;
          }
          
          // Clean up nested brand image references
          if (cleanTile.brand && typeof cleanTile.brand === 'object') {
            const { image, ...brandWithoutImage } = cleanTile.brand;
            cleanTile.brand = brandWithoutImage;
          }
          
          return cleanTile;
        });
        
        console.log(`✅ TilesOutward: ${rawTiles.length} tiles loaded, ${rawTiles.filter(t => t.brand?.image).length} brand images removed`);
        setTiles(processedTiles);
      }

      if (locationsRes.ok) {
        const locationsData = await locationsRes.json();
        setLocations(Array.isArray(locationsData) ? locationsData : locationsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load initial data',
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOutwardHistory = async () => {
    setHistoryLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        userId: user.user._id,
        ...(historyFilters.location && { location: historyFilters.location }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate })
      });

      console.log('Fetching outward history with params:', queryParams.toString());

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/outward-history?${queryParams}`, {
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Outward history response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Outward history fetch error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Outward history response data:', data);

      if (data.success) {
        // Try different possible data structures
        let historyData = data.data?.outwardHistory || data.data?.data || data.data || data.outwardHistory || data.history || [];
        const total = data.data?.total || data.data?.totalCount || data.total || data.totalCount || 0;
        
        console.log('Processed history data:', { historyData, total, isArray: Array.isArray(historyData) });
        
        if (!Array.isArray(historyData)) {
          console.warn('History data is not an array:', historyData);
          historyData = [];
        }
        
        setOutwardHistory(historyData);
        setTotalHistory(total);
      } else {
        console.warn('API returned success: false', data);
        setOutwardHistory([]);
        setTotalHistory(0);
      }
    } catch (error) {
      console.error('Error fetching outward history:', error);
      setOutwardHistory([]);
      setTotalHistory(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({
      location: null,
      startDate: null,
      endDate: null
    });
    setHistoryPage(1);
  };

  const fetchTileStock = async (tileId, locationId) => {
    try {
      const tile = tiles.find(t => t._id === tileId);
      if (!tile || !tile.stock) {
        console.log('Tile not found or no stock for tileId:', tileId);
        return { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 };
      }

      // Try multiple ways to find the location stock
      const locationStock = tile.stock.find(s => {
        const stockLocationId = s.location?._id || s.location;
        return stockLocationId === locationId || 
               stockLocationId?.toString() === locationId?.toString();
      });
      
      if (locationStock) {
        return {
          freshStockBox: locationStock.freshStockBox || 0,
          freshStockPieces: locationStock.freshStockPieces || 0,
          cornerDamagedBox: locationStock.cornerDamagedBox || 0,
          cornerDamagedPieces: locationStock.cornerDamagedPieces || 0
        };
      }
      
      // If not found in local tiles data, try fetching directly from backend
      console.log(`No local stock found for tile ${tile.tilesName} at location ${locationId}, fetching from backend`);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/get-tile/${tileId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.tile && result.tile.stock) {
            const backendLocationStock = result.tile.stock.find(s => {
              const stockLocationId = s.location?._id || s.location;
              return stockLocationId === locationId || 
                     stockLocationId?.toString() === locationId?.toString();
            });
            
            if (backendLocationStock) {
              return {
                freshStockBox: backendLocationStock.freshStockBox || 0,
                freshStockPieces: backendLocationStock.freshStockPieces || 0,
                cornerDamagedBox: backendLocationStock.cornerDamagedBox || 0,
                cornerDamagedPieces: backendLocationStock.cornerDamagedPieces || 0
              };
            }
          }
        }
      } catch (backendError) {
        console.error('Error fetching from backend:', backendError);
      }
      
      console.log(`No stock found for tile ${tile.tilesName} at location ${locationId}`);
      return { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 };
    } catch (error) {
      console.error('Error fetching tile stock:', error);
      return { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 };
    }
  };

  const handleTileSelect = async (tile, index) => {
    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Location',
        text: 'Please select a location first',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    const stock = await fetchTileStock(tile._id, selectedLocation.value);
    
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      tileId: tile._id,
      tileName: tile.tilesName,
      tileDetails: tile,
      availableStock: stock
    };
    setItems(updatedItems);
    setShowTilesModal(false);
    setCurrentItemIndex(null);

    setTimeout(() => {
      freshStockBoxRefs.current[index]?.focus();
    }, 100);
  };

  const addItem = () => {
    setItems([...items, {
      tileId: null,
      tileName: '',
      tileDetails: null,
      freshStockBox: 0,
      freshStockPieces: 0,
      cornerDamagedBox: 0,
      cornerDamagedPieces: 0,
      availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 }
    }]);
  };

  // Refresh available stock for all currently selected tiles by re-fetching
  // the latest tiles list from the backend (so stock numbers reflect any
  // outwards/transfers that happened in other sessions).
  const [refreshingStock, setRefreshingStock] = useState(false);
  const refreshAvailableStock = async () => {
    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Location',
        text: 'Please select a location first to refresh stock.',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }
    setRefreshingStock(true);
    try {
      // Re-fetch tiles list so subsequent fetchTileStock calls see fresh data.
      const tilesRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tiles?userId=${user.user._id}&includeNoStock=true`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (tilesRes.ok) {
        const tilesData = await tilesRes.json();
        const rawTiles = Array.isArray(tilesData) ? tilesData : tilesData.data || [];
        const processedTiles = rawTiles.map(tile => {
          const cleanTile = { ...tile };
          if (cleanTile.brand?.image) delete cleanTile.brand.image;
          if (cleanTile.tilesImage) cleanTile.tilesImage = Boolean(cleanTile.tilesImage);
          if (cleanTile.imageData) delete cleanTile.imageData;
          if (cleanTile.brand && typeof cleanTile.brand === 'object') {
            const { image, ...brandWithoutImage } = cleanTile.brand;
            cleanTile.brand = brandWithoutImage;
          }
          return cleanTile;
        });
        setTiles(processedTiles);

        // Update each item's availableStock from the freshly loaded tiles
        const updatedItems = items.map(item => {
          if (!item.tileId) return item;
          const freshTile = processedTiles.find(t => t._id === item.tileId);
          if (!freshTile || !freshTile.stock) {
            return { ...item, availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 } };
          }
          const locStock = freshTile.stock.find(s => {
            const sid = s.location?._id || s.location;
            return sid?.toString() === selectedLocation.value?.toString();
          });
          return {
            ...item,
            availableStock: {
              freshStockBox: locStock?.freshStockBox || 0,
              freshStockPieces: locStock?.freshStockPieces || 0,
              cornerDamagedBox: locStock?.cornerDamagedBox || 0,
              cornerDamagedPieces: locStock?.cornerDamagedPieces || 0
            }
          };
        });
        setItems(updatedItems);

        Swal.fire({
          icon: 'success',
          title: 'Stock Refreshed',
          text: 'Available stock has been updated for all selected tiles.',
          timer: 1500,
          showConfirmButton: false,
          customClass: { popup: 'swal-dark-mode' }
        });
      } else {
        throw new Error('Failed to refresh tiles');
      }
    } catch (err) {
      console.error('Error refreshing stock:', err);
      Swal.fire({
        icon: 'error',
        title: 'Refresh Failed',
        text: 'Could not refresh available stock. Please try again.',
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setRefreshingStock(false);
    }
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  // Keyboard navigation for input fields
  const handleKeyDown = (e, index, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const fieldOrder = ['freshStockBox', 'freshStockPieces', 'cornerDamagedBox', 'cornerDamagedPieces'];
      const currentFieldIndex = fieldOrder.indexOf(field);
      
      if (currentFieldIndex < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentFieldIndex + 1];
        const refMap = {
          freshStockBox: freshStockBoxRefs,
          freshStockPieces: freshStockPiecesRefs,
          cornerDamagedBox: cornerDamagedBoxRefs,
          cornerDamagedPieces: cornerDamagedPiecesRefs
        };
        refMap[nextField]?.current[index]?.focus();
      } else if (index < items.length - 1) {
        freshStockBoxRefs.current[index + 1]?.focus();
      } else {
        addItem();
        setTimeout(() => {
          freshStockBoxRefs.current[items.length]?.focus();
        }, 100);
      }
    } else if (e.key === 'F2') {
      e.preventDefault();
      setCurrentItemIndex(index);
      setShowTilesModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a location',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    const validItems = items.filter(item => 
      item.tileId && 
      (item.freshStockBox > 0 || item.freshStockPieces > 0 || item.cornerDamagedBox > 0 || item.cornerDamagedPieces > 0)
    );

    if (validItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Valid Items',
        text: 'Please add at least one item with quantity',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    // Auto-convert boxes to pieces when needed and validate stock availability
    for (const item of validItems) {
      const availableStock = item.availableStock;
      const tile = item.tileDetails;
      const piecesPerBox = tile?.tilesPiecesPerBox || 1;
      
      // Handle fresh stock auto-conversion
      let availableFreshBoxes = availableStock.freshStockBox;
      let availableFreshPieces = availableStock.freshStockPieces;
      let requiredFreshBoxes = item.freshStockBox;
      let requiredFreshPieces = item.freshStockPieces;
      
      // Check if we need to convert boxes to pieces
      if (requiredFreshPieces > availableFreshPieces) {
        // Calculate how many pieces we need
        const neededPieces = requiredFreshPieces - availableFreshPieces;
        
        // Check if we have extra boxes available beyond what we need for boxes
        if (availableFreshBoxes > requiredFreshBoxes) {
          console.log(`Converting 1 fresh box to ${piecesPerBox} pieces for ${item.tileName}`);
          
          // DON'T modify the request - validation only
          // We'll let the backend handle the actual conversion
          console.log(`Validated: Will use box conversion for ${item.tileName}`);
          // No changes to item.freshStockBox or item.freshStockPieces here
        } else {
          // Not enough boxes to convert
          Swal.fire({
            icon: 'warning',
            title: 'Insufficient Stock',
            text: `Not enough fresh stock for ${item.tileName}. Available: ${availableStock.freshStockBox} boxes (${availableStock.freshStockPieces} pieces), Required: ${requiredFreshPieces} pieces`,
            customClass: { popup: 'swal-dark-mode' }
          });
          return;
        }
      } else if (item.freshStockBox > availableStock.freshStockBox) {
        // Only validate if the requested boxes exceed what's available
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Stock',
          text: `Not enough fresh stock (box) for ${item.tileName}. Available: ${availableStock.freshStockBox}, Required: ${item.freshStockBox}`,
          customClass: { popup: 'swal-dark-mode' }
        });
        return;
      } 
      // Removed validation for pieces - will be handled by auto-conversion
      
      // Handle corner damaged stock auto-conversion (only corner damaged is sellable)
      let availableDamagedBoxes = availableStock.cornerDamagedBox;
      let availableDamagedPieces = availableStock.cornerDamagedPieces;
      let requiredDamagedBoxes = item.cornerDamagedBox;
      let requiredDamagedPieces = item.cornerDamagedPieces;
      
      // Check if we need to convert boxes to pieces
      if (requiredDamagedPieces > availableDamagedPieces) {
        // Calculate how many pieces we need
        const neededPieces = requiredDamagedPieces - availableDamagedPieces;
        
        // If we have extra damaged boxes available beyond what we need for boxes, convert one
        if (availableDamagedBoxes > requiredDamagedBoxes) {
          console.log(`Converting 1 corner damaged box to ${piecesPerBox} pieces for ${item.tileName}`);
          
          // DON'T modify the request - validation only
          console.log(`Validated: Will use corner damaged box conversion for ${item.tileName}`);
          // No changes to item.cornerDamagedBox or item.cornerDamagedPieces here
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Insufficient Stock',
            text: `Not enough corner damaged stock for ${item.tileName}. Available: ${availableStock.cornerDamagedBox} boxes (${availableStock.cornerDamagedPieces} pieces), Required: ${requiredDamagedPieces} pieces`,
            customClass: { popup: 'swal-dark-mode' }
          });
          return;
        }
      } else if (item.cornerDamagedBox > availableStock.cornerDamagedBox) {
        Swal.fire({
          icon: 'warning',
          title: 'Insufficient Stock',
          text: `Not enough corner damaged stock (box) for ${item.tileName}. Available: ${availableStock.cornerDamagedBox}, Required: ${item.cornerDamagedBox}`,
          customClass: { popup: 'swal-dark-mode' }
        });
        return;
      }
      // Removed validation for damaged pieces - will be handled by auto-conversion
    }

    setSubmitting(true);
    try {
      const outwardData = {
        location: selectedLocation.value,
        date: selectedDate,
        remarks,
        items: validItems.map(item => ({
          tileId: item.tileId,
          freshStockBox: item.freshStockBox,
          freshStockPieces: item.freshStockPieces,
          cornerDamagedBox: item.cornerDamagedBox,
          cornerDamagedPieces: item.cornerDamagedPieces
        })),
        createdBy: user.user._id || user.user.id
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/outward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(outwardData)
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Tiles outward entry created successfully',
          customClass: { popup: 'swal-dark-mode' }
        });
        
        // Reset form
        setItems([{
          tileId: null,
          tileName: '',
          tileDetails: null,
          freshStockBox: 0,
          freshStockPieces: 0,
          cornerDamagedBox: 0,
          cornerDamagedPieces: 0,
          availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0 }
        }]);
        setRemarks('');
        
        // Refresh history
        fetchOutwardHistory();
        
        // Switch to history tab
        setActiveTab('history');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to create outward entry',
          customClass: { popup: 'swal-dark-mode' }
        });
      }
    } catch (error) {
      console.error('Error creating outward entry:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create outward entry',
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (outward) => {
    setSelectedOutward(outward);
    setShowDetailsModal(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Date', 'Location', 'Tile Name', 'Fresh Stock (Box)', 'Fresh Stock (Pieces)', 'Corner Damaged (Box)', 'Corner Damaged (Pcs)', 'Remarks'],
      ...outwardHistory.flatMap(entry => 
        entry.items.map(item => [
          formatDate(entry.date),
          entry.location?.name || '',
          item.tileId?.tilesName || 'N/A',
          item.freshStockBox,
          item.freshStockPieces,
          item.cornerDamagedBox || 0,
          item.cornerDamagedPieces || 0,
          entry.remarks || ''
        ])
      )
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tiles-outward-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Tiles Outward</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Manage tiles outward entries and track stock movement</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              <FaBox className="inline mr-2" />
              New Outward Entry
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              <FaHistory className="inline mr-2" />
              Outward History
            </button>
          </nav>
        </div>

        {/* Entry Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-xl shadow-sm border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Create New Outward Entry</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaMapMarkerAlt className="inline mr-1" />
                    Location *
                  </label>
                  <Select
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    options={locations.map(location => ({
                      value: location._id,
                      label: location.name
                    }))}
                    placeholder="Select location..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isSearchable
                    required
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: darkMode ? '#374151' : 'white',
                        borderColor: darkMode ? '#4b5563' : '#d1d5db',
                        '&:hover': { borderColor: darkMode ? '#6b7280' : '#9ca3af' }
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: darkMode ? '#374151' : 'white'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused ? (darkMode ? '#4b5563' : '#e5e7eb') : 'transparent',
                        color: darkMode ? '#f3f4f6' : '#111827'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: darkMode ? '#f3f4f6' : '#111827'
                      }),
                      input: (base) => ({
                        ...base,
                        color: darkMode ? '#f3f4f6' : '#111827'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: darkMode ? '#9ca3af' : '#6b7280'
                      })
                    }}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaCalendarAlt className="inline mr-1" />
                    Outward Date *
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaStickyNote className="inline mr-1" />
                    Remarks
                  </label>
                  <input
                    type="text"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks..."
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Items</h3>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={refreshAvailableStock}
                      disabled={refreshingStock || !selectedLocation}
                      title="Refresh available stock for all selected tiles"
                      className={`px-4 py-2 rounded-lg transition-colors duration-200 flex items-center ${
                        refreshingStock || !selectedLocation
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                      }`}
                    >
                      <FaSync className={`mr-2 ${refreshingStock ? 'animate-spin' : ''}`} />
                      {refreshingStock ? 'Refreshing...' : 'Refresh Stock'}
                    </button>
                    <button
                      type="button"
                      onClick={addItem}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                    >
                      <FaPlus className="mr-2" />
                      Add Item
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-4">
                        <h4 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Item {index + 1}</h4>
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Tile Selection */}
                        <div className="lg:col-span-1">
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tile *</label>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentItemIndex(index);
                              setShowTilesModal(true);
                            }}
                            className={`w-full px-3 py-2 border rounded-lg hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                          >
                            {item.tileName || 'Select tile...'}
                          </button>
                        </div>

                        {/* Stock inputs */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Fresh Stock (Box)
                          </label>
                          <input
                            ref={el => freshStockBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.freshStockBox}
                            onChange={(e) => handleItemChange(index, 'freshStockBox', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshStockBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                          {item.availableStock && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Available: {item.availableStock.freshStockBox}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Fresh Stock (Pieces)
                          </label>
                          <input
                            ref={el => freshStockPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.freshStockPieces}
                            onChange={(e) => handleItemChange(index, 'freshStockPieces', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshStockPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                          {item.availableStock && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Available: {item.availableStock.freshStockPieces}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Corner Dmg (Box)
                          </label>
                          <input
                            ref={el => cornerDamagedBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.cornerDamagedBox}
                            onChange={(e) => handleItemChange(index, 'cornerDamagedBox', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'cornerDamagedBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                          {item.availableStock && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Available: {item.availableStock.cornerDamagedBox}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Corner Dmg (Pcs)
                          </label>
                          <input
                            ref={el => cornerDamagedPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.cornerDamagedPieces}
                            onChange={(e) => handleItemChange(index, 'cornerDamagedPieces', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'cornerDamagedPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                          {item.availableStock && (
                            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Available: {item.availableStock.cornerDamagedPieces}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting || loading}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors duration-200 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      Create Outward Entry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className={`rounded-xl shadow-sm border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Outward History</h2>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Recent outward entries and tracking - {totalHistory} total entries</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'grid' 
                          ? 'bg-blue-100 text-blue-600 border border-blue-200' 
                          : darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FaTh />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'list' 
                          ? 'bg-blue-100 text-blue-600 border border-blue-200' 
                          : darkMode
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <FaList />
                    </button>
                  </div>
                  
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                  >
                    <FaFilter className="mr-2" />
                    {showFilters ? 'Hide Filters' : 'Filters'}
                  </button>
                  
                  <button
                    onClick={exportToCSV}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center"
                  >
                    <FaFileExport className="mr-2" />
                    Export
                  </button>
                </div>
              </div>

              {/* Enhanced Filters */}
              {showFilters && (
                <div className={`mt-6 p-4 rounded-lg border ${darkMode ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <FaMapMarkerAlt className="inline mr-1" /> Location
                      </label>
                      <Select
                        value={locations.find(loc => loc._id === historyFilters.location) ? {
                          value: historyFilters.location,
                          label: locations.find(loc => loc._id === historyFilters.location)?.name
                        } : null}
                        onChange={(option) => handleHistoryFilterChange('location', option?.value || null)}
                        options={locations.map(location => ({
                          value: location._id,
                          label: location.name
                        }))}
                        placeholder="All locations"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: darkMode ? '#374151' : 'white',
                            borderColor: darkMode ? '#4b5563' : '#d1d5db',
                            '&:hover': { borderColor: darkMode ? '#6b7280' : '#9ca3af' }
                          }),
                          menu: (base) => ({
                            ...base,
                            backgroundColor: darkMode ? '#374151' : 'white'
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused ? (darkMode ? '#4b5563' : '#e5e7eb') : 'transparent',
                            color: darkMode ? '#f3f4f6' : '#111827'
                          }),
                          singleValue: (base) => ({
                            ...base,
                            color: darkMode ? '#f3f4f6' : '#111827'
                          }),
                          input: (base) => ({
                            ...base,
                            color: darkMode ? '#f3f4f6' : '#111827'
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: darkMode ? '#9ca3af' : '#6b7280'
                          })
                        }}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <FaCalendarAlt className="inline mr-1" /> Start Date
                      </label>
                      <input
                        type="date"
                        value={historyFilters.startDate || ''}
                        onChange={(e) => handleHistoryFilterChange('startDate', e.target.value || null)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <FaCalendarAlt className="inline mr-1" /> End Date
                      </label>
                      <input
                        type="date"
                        value={historyFilters.endDate || ''}
                        onChange={(e) => handleHistoryFilterChange('endDate', e.target.value || null)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={resetHistoryFilters}
                        className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 flex justify-center items-center"
                      >
                        <FaUndo className="mr-2" />
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6">
              {historyLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Loading outward history...</p>
                  </div>
                </div>
              ) : outwardHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FaBox className={`mx-auto h-16 w-16 mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>No outward entries found</h3>
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Try adjusting your filters or create a new outward entry.</p>
                </div>
              ) : (
                <>
                  {/* Grid View */}
                  {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {outwardHistory.map((entry) => (
                        <div key={entry._id} className={`border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                          <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                  <FaArrowUp className="text-red-600" />
                                </div>
                                <div>
                                  <h3 className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{entry.outwardId || 'N/A'}</h3>
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(entry.date)}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => setExpandedItems(prev => 
                                  prev.has(entry._id) 
                                    ? new Set([...prev].filter(id => id !== entry._id))
                                    : new Set([...prev, entry._id])
                                )}
                                className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}
                              >
                                {expandedItems.has(entry._id) ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>

                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Location:</span>
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                  {entry.location?.name || 'N/A'}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Items:</span>
                                <span className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{entry.items?.length || 0}</span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Boxes:</span>
                                <span className="font-bold text-red-600">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0), 0) || 0}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Pieces:</span>
                                <span className="font-bold text-red-600">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockPieces || 0) + (item.cornerDamagedPieces || 0), 0) || 0}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created By:</span>
                                <span className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                  {entry.createdBy.name}
                                </span>
                              </div>

                              {expandedItems.has(entry._id) && (
                                <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <div className="space-y-2">
                                    <div className="text-sm">
                                      <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Remarks:</span>
                                      <p className={`mt-1 p-2 rounded ${darkMode ? 'text-gray-300 bg-gray-600' : 'text-gray-700 bg-gray-50'}`}>
                                        {entry.remarks || 'No remarks provided'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                              <button
                                onClick={() => handleViewDetails(entry)}
                                className={`w-full px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                              >
                                <FaEye className="mr-2" />
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* List View */}
                  {viewMode === 'list' && (
                    <div className="overflow-x-auto">
                      <table className={`min-w-full divide-y border rounded-lg ${darkMode ? 'divide-gray-600 border-gray-600' : 'divide-gray-200 border-gray-200'}`}>
                        <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                          <tr>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaHashtag className="inline mr-1" /> Outward ID
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaCalendarAlt className="inline mr-1" /> Date
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaMapMarkerAlt className="inline mr-1" /> Location
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaBoxes className="inline mr-1" /> Items
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaLayerGroup className="inline mr-1" /> Total Boxes
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaLayerGroup className="inline mr-1" /> Total Pieces
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaUser className="inline mr-1" /> Created By
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                              <FaCog className="inline mr-1" /> Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-600' : 'bg-white divide-gray-200'}`}>
                          {outwardHistory.map((entry) => (
                            <tr key={entry._id} className={`transition-colors duration-150 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                {entry.outwardId || 'N/A'}
                              </td>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                {formatDate(entry.date)}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                  {entry.location?.name || 'Unknown'}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                {entry.items?.length || 0}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">
                                <span className="text-red-600 font-bold">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0), 0)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">
                                <span className="text-red-600 font-bold">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockPieces || 0) + (item.cornerDamagedPieces || 0), 0)}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                {entry.createdBy?.name}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <button
                                  onClick={() => handleViewDetails(entry)}
                                  className="text-blue-600 hover:text-blue-800 transition-colors duration-200 flex items-center"
                                >
                                  <FaEye className="mr-1" /> View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Enhanced Pagination */}
                  {totalHistory > historyLimit && (
                    <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                      <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Showing <span className="font-medium">{((historyPage - 1) * historyLimit) + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(historyPage * historyLimit, totalHistory)}</span> of{' '}
                        <span className="font-medium">{totalHistory}</span> entries
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setHistoryPage(1)}
                          disabled={historyPage === 1}
                          className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700 text-gray-300' : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          First
                        </button>
                        <button
                          onClick={() => setHistoryPage(prev => Math.max(prev - 1, 1))}
                          disabled={historyPage === 1}
                          className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700 text-gray-300' : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          <FaChevronLeft />
                        </button>
                        
                        <div className="flex items-center space-x-1">
                          {[...Array(Math.min(5, Math.ceil(totalHistory / historyLimit)))].map((_, i) => {
                            const pageNum = historyPage <= 3 ? i + 1 : historyPage - 2 + i;
                            return pageNum <= Math.ceil(totalHistory / historyLimit) ? (
                              <button
                                key={pageNum}
                                onClick={() => setHistoryPage(pageNum)}
                                className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${
                                  pageNum === historyPage
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : darkMode
                                      ? 'border-gray-600 hover:bg-gray-700 text-gray-300'
                                      : 'border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            ) : null;
                          })}
                        </div>
                        
                        <button
                          onClick={() => setHistoryPage(prev => Math.min(prev + 1, Math.ceil(totalHistory / historyLimit)))}
                          disabled={historyPage >= Math.ceil(totalHistory / historyLimit)}
                          className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700 text-gray-300' : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          <FaChevronRight />
                        </button>
                        <button
                          onClick={() => setHistoryPage(Math.ceil(totalHistory / historyLimit))}
                          disabled={historyPage >= Math.ceil(totalHistory / historyLimit)}
                          className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 disabled:bg-gray-700 disabled:text-gray-500 hover:bg-gray-700 text-gray-300' : 'border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tiles Selection Modal */}
      <TileSelectionModal
        isOpen={showTilesModal}
        onClose={() => { setShowTilesModal(false); setCurrentItemIndex(null); }}
        onSelect={(tile) => handleTileSelect(tile, currentItemIndex)}
        darkMode={darkMode}
        userId={user?.user?._id || user?.user?.id}
        locationId={selectedLocation?.value}
        showNetStock={false}
      />

      {/* Details Modal */}
      {showDetailsModal && selectedOutward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Outward Details - {selectedOutward.outwardId || 'N/A'}</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Outward Information</h4>
                  <div className={`space-y-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div><span className="font-medium">Outward ID:</span> {selectedOutward.outwardId || 'N/A'}</div>
                    <div><span className="font-medium">Date:</span> {formatDate(selectedOutward.date)}</div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Location:</span> 
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {selectedOutward.location?.name || 'N/A'}
                      </span>
                    </div>
                    <div><span className="font-medium">Created By:</span> {selectedOutward.createdBy?.name}</div>
                    <div><span className="font-medium">Total Items:</span> {selectedOutward.items?.length || 0}</div>
                    <div><span className="font-medium">Total Boxes:</span> {selectedOutward.items?.reduce((total, item) => total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0), 0) || 0}</div>
                    <div><span className="font-medium">Total Pieces: </span> {selectedOutward.items?.reduce((total, item) => total + (item.freshStockPieces || 0) + (item.cornerDamagedPieces || 0), 0) || 0} </div>
                  </div>
                </div>
                <div>
                  <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Remarks</h4>
                  <div className={`p-3 rounded-lg text-sm min-h-[100px] ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
                    {selectedOutward.remarks || 'No remarks provided'}
                  </div>
                </div>
              </div>

              <div>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>Outward Items</h4>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y border rounded-lg ${darkMode ? 'divide-gray-600 border-gray-600' : 'divide-gray-200 border-gray-200'}`}>
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Tile</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Fresh Stock</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Corner Damaged</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Total</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-600' : 'bg-white divide-gray-200'}`}>
                      {selectedOutward.items?.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <div>
                              <div className={`font-medium text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{item.tileId?.tilesName || 'N/A'}</div>
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.tileId?.id || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-green-600">
                              {item.freshStockBox || 0} Boxes, {item.freshStockPieces || 0} Pieces
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-yellow-600">
                              {item.cornerDamagedBox || 0} Boxes, {item.cornerDamagedPieces || 0} Pieces
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {(item.freshStockBox + item.cornerDamagedBox) || 0} Boxes
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TilesOutward;